import { useState } from "react";
import type { FormEvent } from "react";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const FORM_ACTION = "https://readdy.ai/api/form/daikh43oh653ivfvof80";

export default function LeakForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [formError, setFormError] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trapValue = String(formData.get("contact_alt") ?? "").trim();
    if (trapValue) {
      setStatus("success");
      setContent("");
      form.reset();
      return;
    }

    formData.delete("contact_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setContent("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <section className="w-full bg-foreground-950">
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-300">
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              我要爆料
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-background-50">
              手里有猛料？匿名告诉我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background-300/80">
              无论是明星动态、行业黑幕还是职场见闻，只要你愿意开口，编辑部都会认真对待。
              线索经核实后将成为全网热瓜，你的身份我们绝对保密。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-background-200/85">
              <li className="flex items-center gap-2">
                <i className="ri-shield-check-line text-secondary-400" aria-hidden="true"></i>
                全程匿名，不收集任何可识别身份信息
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-secondary-400" aria-hidden="true"></i>
                24 小时内完成初步核实与反馈
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-gift-line text-secondary-400" aria-hidden="true"></i>
                被采用的线索将获得专属瓜友身份标识
              </li>
            </ul>
          </div>

          <form
            id="gossip-leak-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-50/10 bg-background-50 p-5 md:p-6"
          >
            <label className="block mb-3">
              <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                线索内容 <span className="text-primary-600">*</span>
              </span>
              <textarea
                name="content"
                required
                maxLength={500}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                rows={5}
                placeholder="请尽量描述清楚时间、地点、涉及的对象，越具体越容易被核实。"
                className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
              <span className="mt-1 block text-right text-xs text-foreground-400">
                {content.length}/500
              </span>
            </label>

            <label className="block mb-4">
              <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                联系方式（选填）
              </span>
              <input
                type="email"
                name="email"
                placeholder="留下邮箱，方便我们跟进核实"
                className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </label>

            <input
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="field-trap"
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? "提交中…" : "提交线索"}
            </button>

            {status === "success" && (
              <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-800">
                <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                提交成功，感谢你的爆料！编辑部会尽快核实。
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-700">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
                {formError}
              </p>
            )}
            <p className="mt-3 text-xs text-foreground-400">
              提交即表示你已阅读并同意本站的免责声明，请勿提交违法或侵犯他人隐私的内容。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}